import { differenceInSeconds } from 'date-fns';
import { getLayerDisplayName, showLayerErrorToast } from '../../Toast/toast';

class RequestCancelledError extends Error {}

export class HttpResponseError extends Error {}

import type { Feature } from 'geojson';
import type { SourceOptions } from './Sources.types';

interface SourceBase<T extends SourceOptions> {
    options: T;
    parseData?(data): Feature[];
    parseDetails?(data): Feature;
}

abstract class SourceBase<T extends SourceOptions = SourceOptions> {
    options: T;
    _timer: ReturnType<typeof setTimeout>;
    _updateInProgress: boolean;
    _pendingUpdatePromise: Promise<unknown>;
    _requestCount = 0;
    _listeningLayers: ((data: unknown) => void)[] = [];
    _lastUpdate = 0;
    _data: Feature[];

    constructor(options: T) {
        this.options = options;
    }

    subscribe(callback: (data: unknown) => void): void {
        if (this._data) {
            callback(this._data);
            if (!this.options.updateInterval) {
                return;
            }
        }

        if (this._listeningLayers.includes(callback)) {
            return;
        }
        this._listeningLayers.push(callback);

        if (!this.options.updateInterval) {
            this._update();
            return;
        }

        if (!this._timer) {
            this._start();
            document.addEventListener('visibilitychange', this._handleVisibilityChange);
        }
    }

    unsubscribe(callback: (data: unknown, source?: SourceBase) => void): void {
        const index = this._listeningLayers.indexOf(callback);

        if (index !== -1) {
            this._listeningLayers.splice(index, 1);
            if (this._listeningLayers.length === 0) {
                document.removeEventListener('visibilitychange', this._handleVisibilityChange);
                this._stop();
            }
        }
    }

    isDataLoaded(): boolean {
        return !!this._data;
    }

    _handleVisibilityChange = (): void => {
        if (document.hidden) {
            this._stop();
        } else {
            this._start();
        }
    };

    _start(): void {
        const secondsSinceLastUpdate = differenceInSeconds(Date.now(), this._lastUpdate);
        const timeUntilFirstUpdate = this.options.updateInterval - secondsSinceLastUpdate;

        if (this._timer) {
            return;
        }

        this._timer = setTimeout(
            () => {
                this._timer = null;
                this._update();
            },
            !!timeUntilFirstUpdate && timeUntilFirstUpdate > 0 ? 1000 * timeUntilFirstUpdate : 0,
        );
    }

    _scheduleUpdate(failed = false) {
        if (this.options.updateInterval) {
            const interval = failed ? (this.options.retryInterval ?? this.options.updateInterval) : this.options.updateInterval;
            this._timer = setTimeout(() => {
                this._timer = null;
                this._update();
            }, 1000 * interval);
        }
    }

    _stop(): void {
        clearTimeout(this._timer);
        this._timer = null;
    }

    _broadcast(): void {
        if (!this._data) {
            return;
        }
        this._listeningLayers.forEach((callback) => callback(this._data));
    }

    getSourceId(): string {
        return this.options.id;
    }

    _update(): void {
        const thisRequestCount = ++this._requestCount;
        let failed = false;

        this.getAjaxPromise()
            .then((data) => {
                if (thisRequestCount !== this._requestCount) {
                    throw new RequestCancelledError();
                }
                return data;
            })
            .then(this.lowercaseProps.bind(this))
            .then((rawData) => {
                this._data = this.parseData ? this.parseData(rawData) : (rawData as Feature[]);
                this._lastUpdate = Date.now();
            })
            .catch((error: unknown) => {
                if (error instanceof RequestCancelledError) {
                    return;
                }
                failed = error instanceof HttpResponseError;
                console.warn(`[${this.options.id}] Update failed:`, error);

                if (this.options.showErrorToast !== false && this._listeningLayers.length > 0) {
                    const layerName = getLayerDisplayName(this.options.displayName, this.options.id);
                    showLayerErrorToast(layerName);
                }
            })
            .finally(() => {
                this._broadcast();
                this._scheduleUpdate(failed);
            });
    }

    lowercaseProps(data: Record<string, unknown> | Record<string, unknown>[]): Record<string, unknown> | Record<string, unknown>[] {
        if (Array.isArray(data)) {
            return data.map(this.lowercaseProps) as Record<string, unknown>[];
        }

        function lowercaseFirstLetter(string: string) {
            return string.charAt(0).toLowerCase() + string.slice(1);
        }

        return Object.keys(data).reduce((destination, key) => {
            destination[lowercaseFirstLetter(key)] = data[key];
            return destination;
        }, {});
    }

    safeMapFeatures<T>(items: T[], mapFn: (item: T) => Feature | null): Feature[] {
        const result: Feature[] = [];
        for (const item of items) {
            try {
                const feature = mapFn(item);
                if (feature != null) {
                    result.push(feature);
                }
            } catch (error) {
                console.warn(`[${this.options.id}] Skipping broken feature:`, error, item);
            }
        }
        return result;
    }

    handleError(error: Error): void {
        error.message = `Failed to get data from source: ${this.options.id}\n${error.message}`;
        throw error;
    }

    abstract getAjaxPromise(...args: unknown[]): Promise<unknown>;
}

export { SourceBase };
